import { useState, useEffect, useCallback } from 'react'

const FOOTER_EXPANSION_KEY = 'fs:footer:expanded'
const hasWindow = typeof window !== 'undefined'
const hasLocalStorage = typeof localStorage !== 'undefined' && localStorage !== null

/**
 * Save a value to localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {any} value - The value to save
 */
const saveToLocalStorage = (key, value) => {
  if (!hasWindow || !hasLocalStorage) return

  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error)
  }
}

/**
 * Hook to manage footer expansion state with localStorage persistence
 * @param {boolean} showArabicExperience - Whether this is a non-member Arabic experience (cannot expand)
 * @returns {[boolean, (value: boolean) => void]} - [isExpanded, setIsExpanded]
 */
export default function useFooterExpansion(showArabicExperience = false) {
  const [isExpanded, setIsExpanded] = useState(() => {
    // For non-member Arabic users, always collapsed - they cannot expand
    if (showArabicExperience) return false

    // For users who can expand the footer, default to expanded (true) on first visit
    const defaultExpanded = true

    // Check localStorage for user preference
    if (!hasWindow || !hasLocalStorage) return defaultExpanded

    try {
      const stored = localStorage.getItem(FOOTER_EXPANSION_KEY)
      return stored !== null ? JSON.parse(stored) : defaultExpanded
    } catch (error) {
      console.error('Failed to read footer expansion preference from localStorage:', error)
      return defaultExpanded
    }
  })

  // Listen to localStorage changes from other tabs/windows
  useEffect(() => {
    if (!hasWindow || !hasLocalStorage) return undefined

    function handleStorageChange(event) {
      if (event.storageArea === localStorage && event.key === FOOTER_EXPANSION_KEY) {
        try {
          // Non-member Arabic users cannot expand, so ignore storage changes
          if (showArabicExperience) return

          const defaultExpanded = true
          const newValue = event.newValue !== null ? JSON.parse(event.newValue) : defaultExpanded
          setIsExpanded(newValue)
        } catch (error) {
          console.error('Failed to parse footer expansion preference from storage event:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [showArabicExperience])

  const setIsExpandedWrapper = useCallback(
    (value) => {
      if (showArabicExperience && value === true) {
        return
      }

      setIsExpanded(value)

      if (!showArabicExperience) {
        saveToLocalStorage(FOOTER_EXPANSION_KEY, value)
      }
    },
    [showArabicExperience]
  )

  return [isExpanded, setIsExpandedWrapper]
}
